import type { EnvironmentReport } from '../types';

/**
 * Captures what the run was taken on, so a number can never be read without its context.
 *
 * ENV-1 makes this load-bearing rather than decorative: desktop Chrome is never acceptance
 * evidence, so a report that does not record *where* it ran cannot be audited later. Everything
 * here is descriptive; nothing here branches behavior. The harness runs identically on desktop —
 * it just labels the output inadmissible.
 */

/**
 * Measures the actual resolution of `performance.now()`.
 *
 * Safari clamps its timers (historically to 1 ms, and further under some privacy settings). Every
 * latency figure the harness reports is quantised to this step, so a 40 ms budget measured with a
 * 1 ms clock has ±1 ms of intrinsic noise and differences smaller than that are not real. Reported
 * rather than assumed, because the clamp has changed between Safari versions.
 */
export function measureTimerResolutionMs(now: () => number = () => performance.now()): number {
  let smallest = Number.POSITIVE_INFINITY;
  // Bounded by iterations, not by wall time, so it cannot spin on a device with a coarse clock.
  for (let attempt = 0; attempt < 20; attempt++) {
    const start = now();
    let next = start;
    let guard = 0;
    while (next === start && guard < 2_000_000) {
      next = now();
      guard++;
    }
    const delta = next - start;
    if (delta > 0 && delta < smallest) smallest = delta;
  }
  return Number.isFinite(smallest) ? smallest : 0;
}

/** True when the page is running as an installed home-screen PWA rather than a Safari tab. */
export function isStandalone(): boolean {
  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone;
  if (legacy === true) return true;
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(display-mode: standalone)').matches
    : false;
}

/**
 * Best-effort iOS Safari detection.
 *
 * Used only to label the run. Deliberately conservative — a false negative marks a genuine
 * on-device run as inadmissible, which is recoverable (the numbers are still there); a false
 * positive would let a desktop run masquerade as evidence, which is not.
 */
export function looksLikeIosSafari(ua: string = navigator.userAgent): boolean {
  const isOtherBrowserOnIos = /CriOS|FxiOS|EdgiOS|OPiOS|Chrome\//.test(ua);
  if (isOtherBrowserOnIos) return false;

  if (/iPhone|iPad|iPod/.test(ua)) return true;

  // iPadOS 13+ reports a desktop Macintosh UA; touch points disambiguate it.
  const isMacUa = /Macintosh/.test(ua) && /Safari/.test(ua);
  return isMacUa && navigator.maxTouchPoints > 1;
}

export function captureEnvironment(): EnvironmentReport {
  return {
    // Substituted at build time (`vite.config.ts` → `define`), so it names the exact bundle rather
    // than anything the running page could have altered.
    buildId: __BUILD_ID__,
    userAgent: navigator.userAgent,
    standalone: isStandalone(),
    looksLikeIosSafari: looksLikeIosSafari(),
    screenW: window.screen?.width ?? 0,
    screenH: window.screen?.height ?? 0,
    viewportW: window.innerWidth,
    viewportH: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    hardwareConcurrency:
      typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null,
    timerResolutionMs: measureTimerResolutionMs(),
    language: navigator.language,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    capturedAt: Date.now(),
  };
}
